import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import AnalyticsHelper from '../helper/AnalyticsHelper';

const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

// ===========================================================================
// CREATOR-CHAT URL ALLOWLIST GATE
//
// The FangateLayout (mounted at /accounts/:id/dashboard, name 'home') is a
// self-contained shell. Settings sub-pages render INLINE via FangateSettings
// — none of the Chat-Accounts / Bots / Labels / My Account clicks trigger a
// router navigation, the URL stays at /dashboard. All the Chatwoot stock
// features (Teams, Custom Attributes, Automation, Macros, Audit Logs,
// Integrations, SLA, Captain, Reports, Campaigns, Portals, HelpCenter,
// Custom Roles, Conversation Workflow, Security, agent-bots, billing,
// general account settings, Contacts, Companies) are still wired in Chatwoot
// upstream routes — without a gate, anyone who guesses a URL like
// `/app/accounts/8/settings/teams` would land on a stock-design page with
// data the user doesn't want exposed.
//
// Strategy: block by URL PATH regex (not route name). Names drift between
// Chatwoot versions; paths are public contracts and stay stable. Anything
// matched here gets silently redirected to /dashboard.
//
// To EXPOSE something currently blocked: add it to the FangateSidebar AND
// remove its pattern from this list.
// ===========================================================================
const BLOCKED_URL_PATTERNS = [
  // Settings sub-pages NOT in our 4-item Settings sub-sidebar
  /\/settings\/(?:teams|automation|macros|audit-logs|attributes|custom-attributes|sla|custom-roles|captain|canned-response|conversation-workflow|security|agent-bots|integrations|applications|assignment-policy|general|billing|sso)(?:$|\/)/,
  // Top-level Chatwoot features not in our left sidebar
  /\/(?:reports|campaigns|portals|helpcenter|captain|contacts|companies)(?:$|\/)/,
  // Alt conversation list views we don't expose (mentions, participating,
  // unattended, custom_view). Specific conversation IDs (/conversations/:id)
  // still resolve normally — they're the only way to deep-link to a chat.
  /\/conversations\/(?:mention|participating|unattended|custom_view|custom\/)/,
];

const isBlockedPath = path => BLOCKED_URL_PATTERNS.some(re => re.test(path));

export const validateAuthenticateRoutePermission = (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  if (to.name === 'no_accounts' || !to.name) {
    return next(frontendURL(`accounts/${accountId}/dashboard`));
  }

  // Stock-Chatwoot URL gate. See BLOCKED_URL_PATTERNS comment above.
  // Redirect rather than 404 so the user lands somewhere sane instead of
  // a blank screen if they typed a URL we don't surface.
  if (isBlockedPath(to.path)) {
    return next(frontendURL(`accounts/${accountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach((to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    userAuthentication.then(() => {
      return validateAuthenticateRoutePermission(to, next, store);
    });
  });
};

export default router;
